import React, { useCallback, useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCcw, Database, UploadCloud, AlertTriangle, UserPlus } from 'lucide-react';
import useOnlineStatus, { setOfflineAllowed } from '../hooks/useOnlineStatus';
import {
  primeScanCache,
  syncPendingScans,
  initOfflineSync,
  onOfflineChange,
  REFRESH_INTERVAL_MS,
} from '../utils/offlineSync';
import {
  countPendingScans,
  countEventRegistrations,
  getMeta,
  listPendingRegistrations,
  listRejected,
  deleteRejected,
  enqueuePendingRegistration,
  putRegistration,
  __internal,
} from '../utils/offlineDb';
import { QUEUE_EVENT } from '../utils/offlineOnSpot';
import { formatTime } from '../utils/datetime';

// Banner for the scan, desk and on-spot pages. While online it uploads queued registrations and scans and refreshes
// the local registration cache every minute; it shows connectivity, cache freshness, what is waiting to upload and
// anything the server refused.
const OfflineScanStatus = ({ eventId, offlineEnabled = true }) => {
  const online = useOnlineStatus();
  const [pending, setPending] = useState(0);
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [rejected, setRejected] = useState([]);
  const [showRejected, setShowRejected] = useState(false);
  const [cached, setCached] = useState(0);
  const [syncedAt, setSyncedAt] = useState(null);
  const [priming, setPriming] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [refreshError, setRefreshError] = useState(null);

  const refreshCounts = useCallback(async () => {
    if (!eventId || !offlineEnabled) return;
    try {
      setPending(await countPendingScans(eventId));
      setPendingRegistrations((await listPendingRegistrations(eventId)).length);
      setRejected(await listRejected(eventId));
      setCached(await countEventRegistrations(eventId));
      const meta = await getMeta(eventId);
      setSyncedAt(meta?.syncedAt || null);
    } catch (_) { /* IndexedDB unavailable */ }
  }, [eventId, offlineEnabled]);

  // Tell the ApiService layer whether offline fallbacks are permitted for this event.
  useEffect(() => {
    setOfflineAllowed(offlineEnabled);
    return () => setOfflineAllowed(false);
  }, [offlineEnabled]);

  useEffect(() => {
    if (!offlineEnabled) return undefined;
    initOfflineSync();
    const unsub = onOfflineChange((evt) => {
      if (evt?.type === 'sync:blocked') setBlocked(evt.reason);
      if (evt?.type === 'sync:done') setBlocked(null);
      refreshCounts();
    });
    window.addEventListener(QUEUE_EVENT, refreshCounts);
    refreshCounts();
    return () => {
      unsub();
      window.removeEventListener(QUEUE_EVENT, refreshCounts);
    };
  }, [offlineEnabled, refreshCounts]);

  // Upload first so the refreshed cache already contains this device's work, then pull other devices' changes.
  const run = useCallback(async ({ full = false } = {}) => {
    setPriming(true);
    setRefreshError(null);
    await syncPendingScans();
    const result = await primeScanCache(eventId, { full });
    if (full && !result?.primed && result?.error) setRefreshError(result.error);
    setPriming(false);
    refreshCounts();
  }, [eventId, refreshCounts]);

  useEffect(() => {
    if (!online || !eventId || !offlineEnabled) return undefined;
    run();
    const timer = setInterval(run, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [online, eventId, offlineEnabled, run]);

  const dismiss = async (item) => {
    await deleteRejected(item.id);
    refreshCounts();
  };

  const retry = async (item) => {
    await enqueuePendingRegistration({ eventId: item.eventId, userId: item.userId, name: item.name, payload: item.payload });
    await putRegistration(__internal.toStoredRegistration(item.eventId, {
      userId: item.userId, formData: item.payload?.formData || {}, source: item.payload?.source, offlinePending: true,
    }));
    await deleteRejected(item.id);
    refreshCounts();
    if (online) run();
  };

  const dismissAll = async () => {
    await Promise.all(rejected.map((item) => deleteRejected(item.id)));
    setShowRejected(false);
    refreshCounts();
  };

  if (!offlineEnabled) return null;

  const tone = online
    ? 'bg-green-50 border-green-200 text-green-800'
    : 'bg-amber-50 border-amber-300 text-amber-900';

  return (
    <div className={`rounded-lg border p-3 text-sm ${tone}`} role="status">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex items-center gap-1 font-medium">
          {online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
          {online ? 'Online' : 'Offline: working from this device'}
        </span>

        <span className="flex items-center gap-1">
          <Database className="h-4 w-4" />
          {cached.toLocaleString()} registrations on this device
          <span className="text-xs opacity-70">
            {syncedAt ? `(updated ${formatTime(syncedAt)})` : '(not downloaded yet)'}
          </span>
        </span>

        {pendingRegistrations > 0 && (
          <span className="flex items-center gap-1">
            <UserPlus className="h-4 w-4" />
            {pendingRegistrations} registration{pendingRegistrations === 1 ? '' : 's'} waiting to upload
          </span>
        )}

        {pending > 0 && (
          <span className="flex items-center gap-1">
            <UploadCloud className="h-4 w-4" />
            {pending} scan{pending === 1 ? '' : 's'} waiting to upload
          </span>
        )}

        {rejected.length > 0 && (
          <button
            type="button"
            onClick={() => setShowRejected((value) => !value)}
            className="flex items-center gap-1 rounded-md bg-red-100 px-2 py-0.5 font-medium text-red-800 hover:bg-red-200"
          >
            <AlertTriangle className="h-4 w-4" />
            {rejected.length} not accepted by the server
          </button>
        )}

        <button
          type="button"
          onClick={() => run({ full: true })}
          disabled={!online || priming}
          className="ml-auto flex items-center gap-1 rounded-md border border-current/20 bg-white/70 px-2.5 py-1 text-xs font-medium hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          title={online ? 'Upload waiting items and download the latest registrations' : 'Needs a connection'}
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${priming ? 'animate-spin' : ''}`} />
          {priming ? 'Updating…' : 'Refresh offline data now'}
        </button>
      </div>

      {blocked === 'signin' && (
        <p className="mt-2 text-xs font-medium text-amber-900">Sign in again to upload the registrations saved on this device.</p>
      )}
      {refreshError && <p className="mt-2 text-xs text-red-700">Couldn't refresh: {refreshError}</p>}

      {showRejected && rejected.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-md border border-red-200 bg-white text-gray-800">
          <div className="flex items-center justify-between border-b bg-red-50 px-3 py-2 text-xs text-red-900">
            <span>These were saved on this device but the server refused them. Check each one at the desk.</span>
            <button type="button" onClick={dismissAll} className="font-medium underline">Dismiss all</button>
          </div>
          <ul className="max-h-64 divide-y overflow-y-auto">
            {rejected.map((item) => (
              <li key={item.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2 text-xs">
                <span className={`rounded px-1.5 py-0.5 font-medium ${item.type === 'registration' ? 'bg-violet-100 text-violet-800' : 'bg-gray-100 text-gray-700'}`}>
                  {item.type === 'registration' ? 'Registration' : `Scan · ${item.purpose}`}
                </span>
                <span className="font-mono">{item.userId}</span>
                {item.name && <span>{item.name}</span>}
                <span className="text-gray-500">{formatTime(item.createdAt)}</span>
                <span className="basis-full text-red-700">{item.error}</span>
                <span className="flex basis-full gap-3">
                  {item.type === 'registration' && (
                    <button type="button" onClick={() => retry(item)} className="font-medium text-blue-700 underline">Try again</button>
                  )}
                  <button type="button" onClick={() => dismiss(item)} className="text-gray-600 underline">Dismiss</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OfflineScanStatus;
